// INV-FIELD: a rich field scopes the existing core transaction; it never owns another Doc or editing producer.
// INV-SLOT: an indexed scope (an option) carries its CRDT slot entity; the index is only where that slot is now.
import type { Blocks } from '@w/content-blocks'
import type { Doc } from '@w/crdt'
import { indexViewBlocks, pageDocument } from '../doc-blocks'
import { isRow } from './tree-read'

export interface CoreScope { blockId: string; field: string; index?: number; slot?: string }
export const FIELD_ROW = '@field'

/** The DOM address of a field (`data-core-scope`): owner, field and index, without the slot identity. */
export function scopeAddress(scope: CoreScope): string {
  return JSON.stringify(scope.index === undefined ? { blockId: scope.blockId, field: scope.field }
    : { blockId: scope.blockId, field: scope.field, index: scope.index })
}

/** One field and slot, wherever it sits now. */
export function sameScope(left: CoreScope | undefined, right: CoreScope | undefined): boolean {
  return left?.blockId === right?.blockId && left?.field === right?.field && left?.slot === right?.slot
}

export function scopePath(doc: Pick<Doc, 'toJSON'>, scope: CoreScope): string {
  const owner = indexViewBlocks(pageDocument(doc)).get(scope.blockId)
  if (!owner) throw new Error(`INV-FIELD: missing owner ${scope.blockId}`)
  return `${owner.path}.${scope.field}${scope.index === undefined ? '' : `.[${scope.index}]`}`
}

/** Enters the field at a DOM address: an indexed slot is bound to the entity at that index (INV-SLOT). */
export function enterScope(doc: Doc, address: string): CoreScope {
  const scope = JSON.parse(address) as CoreScope
  if (scope.index === undefined) return scope
  const owner = indexViewBlocks(pageDocument(doc)).get(scope.blockId)
  const slot = owner && doc.entities(`${owner.path}.${scope.field}`)[scope.index]
  if (!slot) throw new Error(`INV-SLOT: no slot at ${address}`)
  return { ...scope, slot }
}

/** The scope as it exists in `doc` after an edit, undo or remote update: gone with its owner or slot, and an indexed
 * slot re-addressed to wherever its entity now sits. `id` renames identities a remote remap changed. */
export function liveScope(doc: Doc, scope: CoreScope | undefined, id = (value: string) => value): CoreScope | undefined {
  if (!scope) return undefined
  const owner = indexViewBlocks(pageDocument(doc)).get(id(scope.blockId))
  if (!owner?.block[scope.field]) return undefined
  if (scope.slot === undefined) return owner.block.id === scope.blockId ? scope : { ...scope, blockId: owner.block.id }
  const slot = id(scope.slot)
  const index = doc.entities(`${owner.path}.${scope.field}`).indexOf(slot)
  return index < 0 ? undefined : { blockId: owner.block.id, field: scope.field, index, slot }
}

export function scopeBlocks(doc: Doc, scope?: CoreScope): Blocks {
  if (!scope) return pageDocument(doc).content.children as unknown as Blocks
  if ((scope.index === undefined) !== (scope.slot === undefined)) throw new Error('INV-SLOT: an indexed scope carries its slot')
  const owner = indexViewBlocks(pageDocument(doc)).get(scope.blockId)
  if (!owner) throw new Error(`INV-FIELD: scope owner ${scope.blockId} is gone; resolve it with liveScope first`)
  const value = owner.block[scope.field]
  const blocks = (scope.index === undefined ? value : Array.isArray(value) ? value[scope.index] : undefined) as Blocks | undefined
  return fieldBlocks(blocks ?? [])
}

/** A field's content as core blocks. Inline schema slots have no paragraph identity: a view-only row writes directly to their block list. */
export function fieldBlocks(content: Blocks): Blocks {
  return content.some(block => isRow(block) || ['list', 'quote', 'table', 'alert'].includes(block.type))
    ? content : [{ id: FIELD_ROW, type: 'p', children: content }]
}
